import React, {
  createContext, useState, useEffect, useCallback,
  useMemo, useContext,
} from 'react';
import { Grid } from 'antd';
import { FullscreenContextType, ProviderPropsType } from 'contexts/types';

const { useBreakpoint } = Grid;

const FullscreenContext = createContext<FullscreenContextType>({
  fullscreen: false,
  setFullscreen: () => {},
  closeFullscreen: () => {},
  drawerVisible: false,
  setDrawerVisible: () => {},
  closeDrawer: () => {},
});

export const FullscreenProvider = ({ children }: ProviderPropsType) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const closeFullscreen = useCallback(() => setFullscreen(false), []);
  const closeDrawer = useCallback(() => setDrawerVisible(false), []);

  const { md } = useBreakpoint();

  useEffect(() => {
    if (md === false) {
      closeFullscreen();
      closeDrawer();
    }
  }, [md, closeFullscreen, closeDrawer]);

  useEffect(() => {
    if (!fullscreen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeFullscreen();
        closeDrawer();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [fullscreen, closeFullscreen, closeDrawer]);

  const contextValue = useMemo(() => ({
    fullscreen,
    setFullscreen,
    closeFullscreen,
    drawerVisible,
    setDrawerVisible,
    closeDrawer,
  }), [fullscreen, setFullscreen, closeFullscreen, drawerVisible, setDrawerVisible, closeDrawer]);

  return (
    <FullscreenContext.Provider
      value={contextValue}
    >
      {children}
    </FullscreenContext.Provider>
  );
};

export const useFullscreen = () => useContext(FullscreenContext);
